import cn from 'classnames';
import emoji from 'emoji-dictionary';
import Image from 'next/future/image';
import Link from 'next/link';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import htmlParser from 'react-markdown/plugins/html-parser';

import markdownStyle from '../../../css/markdown.module.css';
import CodeBlock from '../../CodeBlock';
import HeadingRenderer from '../../HeadingRenderer';
import ImageRenderer from '../../ImageRenderer';

import styles from './card.module.css';

import Heading from '@components/Heading';
import { loaderProp } from '@utils/random';

const months = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

function formatDate(timestamp) {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  return `${month} ${day}, ${year}`;
}

const Card = ({ post, href, asPath }) => {
  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  const renderers = {
    code: CodeBlock,
    heading: (props) => <HeadingRenderer {...props} baseHref={asPath} />,
    image: ImageRenderer,
    text: emojiSupport,
  };

  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  return (
    <Link href={href} as={asPath}>
      <a>
        <div className="w-full hover:opacity-95">
          {post.coverImage && (
            <div className="w-full mb-4 h-72 relative">
              <Image
                alt="Post cover image"
                className="rounded-lg bg-gray-500 bg-center bg-no-repeat bg-cover"
                fill={true}
                src={post.coverImage.fields.file.url}
                loader={loaderProp}
              />
            </div>
          )}
          <div className="flex flex-col justify-between">
            <div>
              {post.author && (
                <div className="meta hidden flex justify-between items-center mb-3 md:mb-5">
                  <div className="items-center">
                    {post.author.fields.image && (
                      <Image
                        src={post.author.image}
                        className={styles.author_image}
                        alt="Author profile image"
                        width="32"
                        height="32"
                        loader={loaderProp}
                      />
                    )}
                    <p className="ml-2 font-inter text-black font-semibold leading-none text-sm sm:text-base">
                      {post.author.fields.name}
                    </p>
                  </div>
                </div>
              )}

              <div className={`mb-8 ${styles.titleCol}`}>
                <Heading
                  Tag="h2"
                  size="xs"
                  className="hover:opacity-75 mb-2 md:mb-3 font-[700]"
                >
                  {post.title}
                </Heading>
                <p className="text-gray-400 font-medium font-inter text-xs leading-none">
                  {formatDate(post.publishDate)}
                </p>
              </div>

              <p>
                <ReactMarkdown
                  className={cn(
                    markdownStyle.popsql__markdown,
                    styles.postMarkdown,
                  )}
                  source={post.snippet}
                  escapeHtml={false}
                  renderers={renderers}
                  astPlugins={[parseHtml]}
                />
              </p>
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};

export default Card;
